import { Component } from 'react';
import { connect } from 'react-redux';

import Button from 'components/atoms/Button';
import Form from 'components/atoms/Form';
import FormGroup from 'components/atoms/FormGroup';
import Icon from 'components/atoms/Icon';
import Input from 'components/atoms/Input';
import ToggleButton from 'components/atoms/ToggleButton';
import { bindActionCreators, Dispatch } from 'redux';
import { tagCreate } from 'services/tags';
import { SetErrorHandlerAction, setErrorHandlerAction } from 'store/actions/errorsActions';
import { PortType, RootState, UserType } from 'types';
import { MainControlsInterface } from 'types/functions';
import { ActionsType } from 'types/system';
import { renderError, setKey } from 'utils/formHandlers';
import { formatServerErrors } from 'utils/formatServerErrors';
import { translate } from 'utils/translate';
import validation from 'utils/validation';
import { userTypeForPort } from 'utils/permission';

interface UserCreateTagModelProps {
    //unused props
    children?: string | JSX.Element | JSX.Element[];

    //unused props
    language: string;

    id?: string;

    //unused props
    data: any;

    actions: ActionsType | undefined;
    port: PortType;
    user: UserType;
    mainControls: MainControlsInterface;
    setErrorHandlerAction: SetErrorHandlerAction;
}

interface UserCreateTagModelState {
    errors: any;

    //unused state
    data: any | null;

    loading: boolean;
    tag: {
        label: string;
        type: 'public' | 'private';
    };
}

class UserCreateTagModel extends Component<UserCreateTagModelProps, UserCreateTagModelState> {
    constructor(props: UserCreateTagModelProps) {
        super(props);
        this.state = {
            data: null,
            loading: false,
            errors: {},
            tag: {
                label: '',
                type: 'private',
            },
        };
    }

    handleChange = (name: string, value: unknown) => {
        const { tag, errors } = this.state;
        setKey(tag, name, value);
        setKey(errors, name, null);
        this.setState({ tag, errors });
    };

    handleFormCancel = () => {
        this.props.actions?.onCancel();
    };

    handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e?.preventDefault();
        this.setState({ errors: {}, loading: true }, () => {
            const validationClass = new validation([{ name: 'label', rules: ['required'] }]);
            const { errors, status } = validationClass.check(this.state.tag);
            if (status) {
                this.setState({ errors, loading: false });
                return;
            }

            tagCreate(this.props.port.id, { ...this.state.tag, tag: this.state.tag.label })
                .then(() => {
                    this.props.mainControls.refreshTaglist();
                    this.props.actions?.onCancel();
                })
                .catch((errors) => {
                    switch (errors.response?.status) {
                        case 422:
                            this.setState({ errors: formatServerErrors(errors), loading: false });
                            break;
                        default:
                            this.setState({ loading: false });
                            this.props.setErrorHandlerAction(errors);
                            break;
                    }

                    return;
                });
        });
    };

    render() {
        return (
            <>
                <Form onSubmit={this.handleFormSubmit} className="bg-white shadow p-4 w-full rounded">
                    <div id={`Drag-${this.props.id}`} className="flex justify-between mb-2 no-select ">
                        <div className="text-lg leading-6 font-bold">{translate('Create tag')} </div>
                        <div
                            onTouchEnd={this.handleFormCancel}
                            onClick={this.handleFormCancel}
                            className="cursor-pointer"
                        >
                            <Icon>
                                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                <path d="M18 6l-12 12" />
                                <path d="M6 6l12 12" />
                            </Icon>
                        </div>
                    </div>
                    <FormGroup
                        label={translate('Tag')}
                        comments={renderError(this.state.errors?.label || this.state.errors?.tag)}
                        required={true}
                    >
                        <Input name="label" value={this.state?.tag.label} onChange={this.handleChange} />
                    </FormGroup>

                    {userTypeForPort(this.props.user, this.props.port.id) !== 'guest' ? (
                        <div className="mt-5 flex items-center gap-1">
                            <div className="flex items-center align-middle">
                                <ToggleButton
                                    onChange={(name, value) => {
                                        //@ts-ignore
                                        this.handleChange(name, value ? 'public' : 'private');
                                    }}
                                    name="type"
                                    value={this.state.tag.type === 'public'}
                                ></ToggleButton>
                            </div>
                            <div className="flex items-center gap-2  ">
                                <div>{translate('Make this tag available for all users')}</div>
                                <Icon className="w-4 h-4">
                                    <path d="M9 7m-4 0a4 4 0 1 0 8 0a4 4 0 1 0 -8 0"></path>
                                    <path d="M3 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2"></path>
                                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                                    <path d="M21 21v-2a4 4 0 0 0 -3 -3.85"></path>
                                </Icon>
                            </div>
                        </div>
                    ) : (
                        <div className="mt-5 flex items-center gap-1 text-red-500">
                            {translate('You cannot create shared tags')}
                        </div>
                    )}

                    <div className="flex justify-end">
                        <div className="mt-5 flex gap-3">
                            <Button
                                type="button"
                                className="px-4 py-1 font-bold   border border-gray-400"
                                onClick={this.handleFormCancel}
                            >
                                {translate('Cancel')}
                            </Button>
                            <Button
                                disabled={this.state.loading}
                                loading={this.state.loading}
                                type="submit"
                                className="px-4 py-2 bg-primary-submit rounded-md text-white"
                            >
                                {translate('Save')}
                            </Button>
                        </div>
                    </div>
                </Form>
            </>
        );
    }
}
const mapStateToProps = (state: RootState) => ({
    language: state.user.user.language,
    mainControls: state.layout.mainControls,
    port: state.port.port,
    user: state.user.user,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    setErrorHandlerAction: bindActionCreators(setErrorHandlerAction, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(UserCreateTagModel);
